// The audio player on the canvas (spec §19.1): a white panel with a round play
// button, the title, the waveform and the length. Laid out in a 320×80 box
// that's scaled to the object's size, so the whole player scales as one.
import type { Audio } from '../../../shared/types.ts';
import { AUDIO, formatDuration } from '../../../shared/audio.ts';
import { FONT_FAMILY } from '../../text/layout.ts';

const ACCENT = '#4262FF';
const BORDER = 'rgba(0, 0, 0, 0.12)';
const SHADOW = 'rgba(0, 0, 0, 0.07)';
const WAVE = '#C3C9D6';
const TITLE = '#1A1A1A';
const MUTED = '#6B6B6B';

/** The layout, in the 320×80 box. */
export const AUDIO_LAYOUT = {
  radius: 14,
  button: { cx: 40, cy: 40, r: 22 },
  title: { x: 76, baseline: 31, size: 14 },
  wave: { x: 76, w: 228, cy: 54, half: 11 },
  right: 304,
} as const;

/** From object-local units to the layout box. */
export const layoutScale = (o: Audio): [number, number] => [o.w / AUDIO.width, o.h / AUDIO.height];

/** What's at a point in the layout box: the button, the waveform (with how far along it), or neither. */
export function audioPart(u: number, v: number): { part: 'button' } | { part: 'wave'; frac: number } | null {
  const { button: b, wave: w } = AUDIO_LAYOUT;
  if ((u - b.cx) ** 2 + (v - b.cy) ** 2 <= (b.r + 2) ** 2) return { part: 'button' };
  if (u >= w.x - 4 && u <= w.x + w.w + 4 && v >= w.cy - w.half - 6 && v <= w.cy + w.half + 6) {
    return { part: 'wave', frac: Math.min(1, Math.max(0, (u - w.x) / w.w)) };
  }
  return null;
}

/** Bars of the waveform: x and half-height of each, in the layout box. A clip without peaks is flat. */
export function waveBars(peaks: readonly number[]): [number, number][] {
  const { wave } = AUDIO_LAYOUT;
  const n = AUDIO.peaks;
  const step = wave.w / n;
  return Array.from({ length: n }, (_, i) => {
    const p = peaks.length ? peaks[Math.min(peaks.length - 1, Math.floor((i * peaks.length) / n))]! : 0;
    return [wave.x + step * (i + 0.5), Math.max(1.2, p * wave.half)];
  });
}

/** Draws the player in its local frame (the caller sets the transform). */
export function drawAudio(ctx: CanvasRenderingContext2D, o: Audio, lod: boolean): void {
  const [sx, sy] = layoutScale(o);
  ctx.save();
  ctx.scale(sx, sy);
  const { radius } = AUDIO_LAYOUT;
  ctx.fillStyle = SHADOW;
  ctx.beginPath();
  ctx.roundRect(0, 2, AUDIO.width, AUDIO.height, radius);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(0, 0, AUDIO.width, AUDIO.height, radius);
  ctx.fill();
  ctx.stroke();
  drawButton(ctx, false);
  if (lod) {
    ctx.restore();
    return;
  }

  const { title, right } = AUDIO_LAYOUT;
  ctx.textBaseline = 'alphabetic';
  ctx.font = `500 12px ${FONT_FAMILY}`;
  const length = formatDuration(o.duration);
  const lengthW = ctx.measureText(length).width;
  ctx.fillStyle = MUTED;
  ctx.textAlign = 'right';
  ctx.fillText(length, right, title.baseline);
  ctx.font = `600 ${title.size}px ${FONT_FAMILY}`;
  ctx.fillStyle = TITLE;
  ctx.textAlign = 'left';
  ctx.fillText(fit(ctx, o.title || 'Audio', right - lengthW - 10 - title.x), title.x, title.baseline);

  ctx.strokeStyle = WAVE;
  drawBars(ctx, waveBars(o.peaks), 0, Infinity);
  ctx.restore();
}

/**
 * The clip that's playing (or paused part-way), drawn over the static one on the interactive
 * layer (spec §19.2): the played part of the waveform in colour, and pause in place of play.
 * World transform set by the caller.
 */
export function drawAudioProgress(ctx: CanvasRenderingContext2D, o: Audio, fraction: number, playing: boolean, lod: boolean): void {
  ctx.save();
  ctx.translate(o.x + o.w / 2, o.y + o.h / 2);
  ctx.rotate(o.rotation);
  ctx.translate(-o.w / 2, -o.h / 2);
  const [sx, sy] = layoutScale(o);
  ctx.scale(sx, sy);
  drawButton(ctx, playing);
  if (!lod) {
    const { wave, right, title } = AUDIO_LAYOUT;
    const bars = waveBars(o.peaks);
    ctx.strokeStyle = ACCENT;
    drawBars(ctx, bars, 0, wave.x + wave.w * fraction);
    // The time so far counts up in place of the length (the panel's white hides what's beneath).
    ctx.font = `500 12px ${FONT_FAMILY}`;
    const text = formatDuration(o.duration * fraction);
    const w = Math.max(ctx.measureText(text).width, ctx.measureText(formatDuration(o.duration)).width);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(right - w - 2, title.baseline - 13, w + 3, 17);
    ctx.fillStyle = ACCENT;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, right, title.baseline);
  }
  ctx.restore();
}

function drawButton(ctx: CanvasRenderingContext2D, playing: boolean): void {
  const { cx, cy, r } = AUDIO_LAYOUT.button;
  ctx.fillStyle = ACCENT;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  if (playing) {
    ctx.roundRect(cx - 7, cy - 8, 5, 16, 1.5);
    ctx.roundRect(cx + 2, cy - 8, 5, 16, 1.5);
  } else {
    ctx.moveTo(cx - 5, cy - 9);
    ctx.lineTo(cx + 9, cy);
    ctx.lineTo(cx - 5, cy + 9);
    ctx.closePath();
  }
  ctx.fill();
}

/** Bars whose x is in [from, to). */
function drawBars(ctx: CanvasRenderingContext2D, bars: [number, number][], from: number, to: number): void {
  const { cy } = AUDIO_LAYOUT.wave;
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (const [x, half] of bars) {
    if (x < from || x >= to) continue;
    ctx.moveTo(x, cy - half);
    ctx.lineTo(x, cy + half);
  }
  ctx.stroke();
}

/** `text`, shortened with an ellipsis to fit `max` (font already set). */
function fit(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(`${text.slice(0, mid)}…`).width <= max) lo = mid;
    else hi = mid - 1;
  }
  return `${text.slice(0, lo).trimEnd()}…`;
}
